import {Component, OnInit, ViewChild} from '@angular/core';
import {Event} from "../models/event";
import {Organization} from "../models/organization";
import {EventService} from "../services/event.service";
import {OrganizationService} from "../services/organization.service";
import {RegistrationService} from "../services/registration.service";
import {ActivatedRoute, Router} from "@angular/router";
import {firstValueFrom, Observable} from "rxjs";
import {User} from "../models/user";
import {AuthenticationService} from "../services/authentication.service";
import {MatDialog, MatDialogConfig} from "@angular/material/dialog";
import {
  DescriptionStrategy,
  Image, LoadingType,
  ModalGalleryConfig,
  ModalGalleryRef,
  ModalGalleryService
} from "@ks89/angular-modal-gallery";
import {SignUpModalComponent} from "../signup-modal/signup-modal.component";
import {GeocodingService} from "../services/geocoding.service";
import {GeocoderResponse} from "../models/geocoder-response";

@Component({
    selector: 'app-event-page',
    templateUrl: './event-page.component.html',
    styleUrls: ['./event-page.component.css'],
    standalone: false
})
export class EventPageComponent implements OnInit {
  @ViewChild('googleMap', { static: false }) mapDiv!: 'googleMap';

  user!: User;
  event$!: Observable<Event>;
  signedUp!: boolean;
  organization$!: Observable<Organization>;
  images: Image[] = [];
  map!: google.maps.Map;

  constructor(
    private eventService: EventService,
    private orgService: OrganizationService,
    private authService: AuthenticationService,
    private regService: RegistrationService,
    private route: ActivatedRoute,
    private modalGalleryService: ModalGalleryService,
    private geoService: GeocodingService,
    private dialog: MatDialog,
    private router: Router
  ) {
  }

  async ngOnInit() {
    try {
      const params = await firstValueFrom(this.route.paramMap);
      const ref = params.get('id');

      if (ref != null) {
        this.event$ = this.eventService.getEventByRef(ref);
        this.event$.subscribe(event => {
          let i = 0;
          for (const image of event.images) {
            this.images.push(new Image(i++, {img: image}))
          }

          this.organization$ = this.orgService.getOrganizationByRef(event.organization);
          this.geoService.addressToCoords(event.address).subscribe(
            (response: GeocoderResponse) => {
              if (response.status === 'OK' && response.results?.length) {
                const location = response.results[0];
                const loc = location.geometry.location;

                const interval = setInterval(() => {
                  const element = this.mapDiv;
                  if (element) {
                    this.initMap(loc);
                    clearInterval(interval);
                  }
                }, 100);
              }
          });
        });

        this.user = await this.authService.getUser();
        if (this.user) {
          this.signedUp = await this.regService.checkUserRegistered(this.user.uid, ref);
        }
      }
    } catch (error) {
      console.error("Error occurred: ", error);
    }
  }

  async initMap(loc: google.maps.LatLng): Promise<void> {
    const { Map } = await google.maps.importLibrary("maps") as google.maps.MapsLibrary;
    const { AdvancedMarkerElement } = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;

    this.map = new Map(document.getElementById("map") as HTMLElement, {
      center: loc,
      zoom: 14,
      mapId: 'a89d4d91c0021e9',
      disableDefaultUI: true,
      disableDoubleClickZoom: true,
      zoomControl: true,
      fullscreenControl: true,
    });

    new AdvancedMarkerElement({
      map: this.map,
      position: loc,
      title: 'Activiteit Locatie'
    });
  }

  /**
   * Initializes and displays the sign-up modal for the user using the sign-up modal component
   *
   * @param eventID
   */
  presentSignUpModal(eventID: string) {
    if (!this.signedUp) {
      const dialogConfig = new MatDialogConfig();

      dialogConfig.disableClose = true;
      dialogConfig.data = {
        loggedIn: Boolean(this.user),
        type: "Login"
      }

      const dialogRef = this.dialog.open(SignUpModalComponent, dialogConfig);
      dialogRef.afterClosed().subscribe(
        data => {
          if (data) {
            this.signUp(eventID);
          }
        }
      );
    } else {
      this.regService.removeRegistration(eventID, this.user.uid).then(() => {
        this.signedUp = false;
      });
    }
  }

  /**
   * Initializes and displays the sign-up modal for the user using the sign-up modal component
   *
   * @param eventID
   */
  presentDeleteModal(eventID: string) {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.data = {
      loggedIn: Boolean(this.user),
      type: "Delete"
    }

    const dialogRef = this.dialog.open(SignUpModalComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(
      data => {
        if (data) {
          this.eventService.deleteEvent(eventID).then(() => {
              this.router.navigateByUrl('activiteiten', { replaceUrl: true });
            }
          );
        }
      }
    );
  }

  /**
   * Signs user up for selected event
   *
   * @param eventID
   */
  signUp(eventID: string) {
    this.regService.addRegistration(eventID, this.user.uid).then(() => {
      this.signedUp = true;
    });
  }

  /**
   * Redirects user to the activity change page
   *
   * @param eventID
   */
  editEvent(eventID: string) {
    this.router.navigateByUrl('activiteit/wijzigen/' + eventID, { replaceUrl: true });
  }

  /**
   * Initializes and opens the image modal
   *
   * @param id
   * @param imageIndex
   */
  openModal(id: number, imageIndex: number): void {
    const dialogRef: ModalGalleryRef = this.modalGalleryService.open({
      id: id,
      images: this.images,
      currentImage: this.images[imageIndex],
      libConfig: {
        loadingConfig: {
          enable: false,
          type: LoadingType.STANDARD
        },
        currentImageConfig: {
          description: {
            strategy: DescriptionStrategy.ALWAYS_HIDDEN,
          }
        }
      }
    } as ModalGalleryConfig) as ModalGalleryRef;
  }
}
